import { APP_INITIALIZER, inject, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CookieService } from './core/services/cookie.service';
import { InfinityModule } from './shared/infinity.module';
import { AuthGuard } from './core/guards/auth-guard';
import { Observable, forkJoin, map } from 'rxjs';
import { MatGoogleMapsAutocompleteModule } from '@angular-material-extensions/google-maps-autocomplete';
import { KeycloakAngularModule, KeycloakBearerInterceptor, KeycloakService } from 'keycloak-angular';
import { environment } from '../environments/environment';
import { AuthnService } from '@modules/authn/services/authn.service';
import { AuthService } from '@core/services/auth.service';
import { KeycloakAuthService } from '@core/services/keycloak-auth.service';
import { UserInfoService } from '@core/services/user-info.service';
import { LoadingService } from '@core/services/loading.service';
import { Router } from '@angular/router';

export class MultiHttpLoader implements TranslateLoader {
	constructor(
		private http: HttpClient,
		public resources: string[],
	) {}

	getTranslation(lang: string): Observable<any> {
		const requests = this.resources.map((path) => this.http.get(`${path}${lang}.json`));
		return forkJoin(requests).pipe(map((response) => Object.assign({}, ...response)));
	}
}

registerLocaleData(localeEs);

export function HttpLoaderFactory(http: HttpClient) {
	return new MultiHttpLoader(http, [
		'assets/i18n/', // Ruta de la app
		'/i18n/', // Ruta de la librería
	]);
}

function initializeKeycloak(keycloak: KeycloakService) {
	const router = inject(Router);
	return () => {
		if (!environment.enableKeycloak) {
			return Promise.resolve(true);
		}

		return keycloak
			.init({
				config: {
					url: environment.keycloak.url,
					realm: environment.keycloak.realm,
					clientId: environment.keycloak.clientId,
				},
				initOptions: {
					onLoad: 'check-sso',
					silentCheckSsoRedirectUri: window.location.origin + '/assets/keycloak/silent-check-sso.html',
					checkLoginIframe: false,
					flow: 'standard',
					redirectUri: window.location.href,
				},
				bearerExcludedUrls: ['/assets'],
				enableBearerInterceptor: false,
				loadUserProfileAtStartUp: true,
			})
			.catch((error) => {
				// Logout si el token expira en keycloak
				localStorage.removeItem('role');
				localStorage.removeItem('access_token');
				router.navigate(['login']);
				console.error('Keycloak initialization error', error);
			});
	};
}

@NgModule({
	declarations: [AppComponent],
	imports: [
		BrowserModule,
		BrowserAnimationsModule,
		AppRoutingModule,
		HttpClientModule,
		FormsModule,
		ReactiveFormsModule,
		InfinityModule,
		MatGoogleMapsAutocompleteModule.forRoot('AIzaSyC7Opl5MZyhe_lDM2L-NGQwm42OxG_dACc'),
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: HttpLoaderFactory,
				deps: [HttpClient],
			},
		}),
		KeycloakAngularModule,
	],
	providers: [
		CookieService,
		AuthGuard,
		{ provide: LOCALE_ID, useValue: 'es' },
		{
			provide: APP_INITIALIZER,
			useFactory: initializeKeycloak,
			multi: true,
			deps: [KeycloakService],
		},
		KeycloakService,
		// {
		// 	provide: HTTP_INTERCEPTORS,
		// 	useClass: KeycloakBearerInterceptor,
		// 	multi: true,
		// },
	],
	bootstrap: [AppComponent],
})
export class AppModule {}
